import {
  formatMoney,
  HttpClient,
  queryParams,
  type ClientOptions,
  type AttachmentUpload,
  type DisputeEvidenceItem,
  type DisputeRaisedContext,
  type CohortMatrixWire,
  type CohortPlayerWire,
  type Envelope,
} from '@affdice/portal-kit';

import { nextRequestSeq, noActiveProgramme, notifyNoActiveProgramme } from './closed-programme.ts';
import type { CalculationDealInput, CalculationOutcomesInput } from './screens.ts';

/**
 * §26.12 — the two money figures on a spend declaration, formatted here
 * rather than in `spend.tsx`: `check:boundaries` refuses a `.tsx` file that
 * calls `formatMoney()` itself (§1.3a — money is formatted once, in the
 * view-model layer, so two screens cannot each round it differently). This
 * task's file list has no `screens.ts` to be that layer (the same reason
 * `formatSubAccountAmount` lives in the operator's own `client.ts`), so the
 * figures this screen needs formatted are formatted here instead.
 */
export function formatSpendAmount(amount_minor: string, currency: string): string {
  return formatMoney({ amount_minor, currency });
}

/**
 * The affiliate's endpoints.
 *
 * Only theirs. The transport, the error shape and the §0.8d rule-id handling
 * come from the kit; what is declared here is the surface an affiliate is
 * allowed to reach, and nothing an operator or platform staff can.
 */
/** #46 — one smart link, with what is in its pool. */
export interface SmartLinkMember {
  readonly id: string;
  readonly position: number;
  readonly campaign_id: string | null;
  readonly category_id: string | null;
  readonly name: string | null;
  readonly kind: 'campaign' | 'category';
  /**
   * Whether a click could go there today, as three answers rather than two:
   * true is paired and on, false is paired and withdrawn, and null is no
   * pairing row at all — which does not block (0138). Always null for a
   * category, which is a set rather than a pairing.
   */
  readonly participating: boolean | null;
}

export interface SmartLinkRow {
  readonly id: string;
  readonly name: string;
  readonly selection: string;
  readonly status: string;
  readonly created: string | null;
  readonly token: string | null;
  /** Composed by the API: a pool can span brands, so the screen cannot. */
  readonly url: string | null;
  readonly members: readonly SmartLinkMember[];
  readonly clicks_30d: number;
  /** Clicks no campaign in the pool would take — #46's NO_ELIGIBLE_CAMPAIGN. */
  readonly unmatched_30d: number;
}

/** §7.13 — where one component of a deal pays. No rows for a component: everywhere. */
export interface CoverageRow {
  readonly component: 'CPA' | 'REVSHARE';
  readonly mode: 'INCLUDE' | 'EXCLUDE';
  readonly jurisdiction: string;
}

/** QA round 2 F4 — one brand's carried revenue deficit (`/v1/statements`), negative while owed back. */
export interface CarryoverDeficit {
  readonly brand_id: string;
  readonly brand: string;
  readonly currency: string;
  readonly deficit_minor: string;
}

/**
 * §7.12d — one deal version, in the shape the campaign terms view reads it.
 *
 * The same summary the operator's own campaign screens are built from, so an
 * affiliate and the operator who set the terms read one set of fields rather
 * than two retellings that can drift apart.
 */
export interface TermsSummary {
  readonly deal_id: string;
  readonly deal_version_id: string;
  readonly version: number;
  readonly state: string;
  readonly valid_from: string;
  /** CPA, REVSHARE, HYBRID — or a newer plan this build has no words for. */
  readonly commission_type: string;
  readonly cpa_trigger: 'FTD' | 'REGISTRATION' | 'CUMULATIVE_DEPOSIT' | 'DEPOSIT';
  readonly revenue_basis: 'NGR' | 'GGR' | 'FTD_VALUE' | 'FTD_COUNT' | 'DEPOSITS';
  readonly cpa_amount_minor: string | null;
  readonly cpa_currency: string | null;
  readonly revshare_rate_bps: number | null;
  readonly revshare_amount_per_unit_minor: string | null;
  /** The per-FTD amount's own currency; absent from a server older than this field. */
  readonly revshare_amount_per_unit_currency?: string | null;
  /** RECURRING_CPA: months the payment keeps running; null/absent = not stated. */
  readonly end_condition_months?: number | null;
  readonly cpc_amount_minor?: string | null;
  readonly cpc_currency?: string | null;
  readonly cpc_period_cap?: number | null;
  readonly cpm_amount_minor?: string | null;
  readonly cpm_currency?: string | null;
  readonly cpm_period_cap?: number | null;
  /** §24.10a — in `base_currency`, which is what the thresholds are set in. */
  readonly cpa_cumulative_deposit_minor: string | null;
  readonly cpa_cumulative_wagering_minor: string | null;
  readonly cpa_qualification_deadline_days: number | null;
  /** §24.3b — what the deadline counts from; absent on older answers means signup. */
  readonly cpa_qualification_from?: 'SIGNUP' | 'FIRST_DEPOSIT' | null;
  readonly hybrid_mode: string | null;
  readonly bonus_payout_enabled: boolean;
  readonly bonus_payout_is_percentage: boolean;
  readonly bonus_payout_basis: string | null;
  readonly bonus_payout_rate_bps: number | null;
  readonly bonus_payout_amount_minor: string | null;
  readonly bonus_payout_currency: string | null;
  readonly base_currency: string;
  readonly coverage: readonly CoverageRow[];
}

/**
 * §7.12 — which of three things pays an affiliate on one campaign: the
 * campaign's own payout, their default deal with the brand, or terms agreed
 * for that campaign alone (which pay only once the affiliate has accepted).
 */
export type TermsSource = 'CAMPAIGN' | 'DEFAULT' | 'CUSTOM';

/**
 * §41.7 — one registered postback, as the list screen reads it. Counts are
 * already rolled up by status server-side, so the table needs no client-side
 * tally over a delivery list it has not fetched.
 */
export interface PostbackRegistration {
  readonly id: string;
  readonly event_type: string;
  readonly url: string;
  readonly enabled: boolean;
  readonly validated_at: string;
  readonly delivered: number;
  readonly pending: number;
  readonly failed: number;
  readonly exhausted: number;
  readonly withheld: number;
  readonly last_attempt_at: string | null;
  readonly last_delivered_at: string | null;
}

/** §41.7b — one delivery attempt against a registered postback. */
export interface PostbackDelivery {
  readonly id: string;
  readonly status: string;
  readonly attempts: number;
  /** Why the latest attempt failed; null when it succeeded or none was made. */
  readonly last_error: string | null;
  /** The HTTP code the endpoint answered with, when it answered. */
  readonly response_status: number | null;
  readonly delivered_at: string | null;
  readonly withheld_at: string | null;
  readonly last_attempt_at: string | null;
  readonly next_attempt_at: string | null;
  /** The conversion's own status at send time, from the payload. */
  readonly conversion_status: string | null;
  readonly goal: string | null;
  /** From the payload; null when the conversion carried none. */
  readonly click_id: string | null;
}

/** §41.7b — one signed test request. Writes nothing: not a delivery. */
export interface PostbackTestResult {
  readonly reached: boolean;
  readonly detail: string;
  readonly status?: number;
  readonly ms?: number;
  readonly body_prefix?: string;
}

export interface CampaignTerms {
  readonly campaign_id: string;
  readonly campaign_name: string;
  /** Null when nothing pays this affiliate on the campaign yet. */
  readonly applies: { readonly source: TermsSource; readonly terms: TermsSummary | null } | null;
  /** §7.11 — an offer for this campaign alone, waiting for their answer. */
  readonly pending: { readonly deal_id: string; readonly terms: TermsSummary | null } | null;
  /**
   * §7.2a — what this campaign's payout has been, newest first, the live one
   * included, with the run each covered. Empty unless the campaign's own
   * payout is what pays you: a default deal's history belongs to the deal.
   */
  readonly payout_history?: readonly (TermsSummary & { ends_at: string | null })[];
  readonly note: string;
}

/* ------------------------------------------------------------------ *
 * §8.9, §12.3 — self-service sub-affiliate recruiting
 * ------------------------------------------------------------------ */

/**
 * §8.9a — why this affiliate cannot recruit right now, when they cannot.
 * `NO_APPLICATION_PAGE` — the operator has no public application page (no
 * signup code), so a referral link would have nowhere to send anyone.
 */
export type SubAffiliateWhyNot = 'PROGRAMME_OFF' | 'NOT_ACTIVE' | 'DEPTH_LIMIT' | 'NO_APPLICATION_PAGE';

/**
 * §12.3 — the affiliate's own recruiting surface: the link, the terms a
 * referral is made at, and the §8.9e view of who they have recruited.
 */
export interface SubAffiliatesReport {
  readonly programme: {
    readonly enabled: boolean;
    readonly can_recruit: boolean;
    readonly why_not: SubAffiliateWhyNot | null;
    /** §8.2 — this recruiter's own rate, or the operator's default. */
    readonly override_bps: number;
    readonly basis: 'SUB_COMMISSION' | 'SUB_NGR';
    readonly lifetime: 'FIXED_TERM' | 'LIFETIME' | 'ACTIVE_ONLY';
    readonly fixed_term_months: number;
    /** §8.9d — 20 in a rolling 24 hours; how many of those are left. */
    readonly invitations_left_today: number;
  };
  readonly referral_code: string | null;
  /** `/apply/<code>?ref=…` (the application page) — null until a code exists. */
  readonly signup_path: string | null;
  /**
   * §8.9e — direct recruits, pending ones too (`linked: false`). No names:
   * `key` identifies the row for React, never for display.
   */
  readonly subs: readonly {
    readonly key: string;
    readonly joined_at: string;
    readonly status: string;
    readonly linked: boolean;
    readonly clicks: number;
    readonly ftds: number;
    readonly override_earned: readonly { currency: string; amount_minor: string }[];
  }[];
  readonly invitations: readonly {
    readonly email: string;
    readonly sent_at: string;
    readonly joined: boolean;
  }[];
}

export class AffiliateApi extends HttpClient {
  readonly apiBaseUrl: string;

  constructor(options: ClientOptions) {
    super(options);
    this.apiBaseUrl = options.baseUrl.replace(/\/+$/, '');
  }

  /**
   * 0332 — every call passes through here, so a NO_ACTIVE_PROGRAMME answer at
   * sign-in or mid-use reaches the portal's root, which shows the closed
   * programme page instead of leaving each screen to print the refusal.
   */
  override async request<T>(
    method: string,
    path: string,
    init: Parameters<HttpClient['request']>[2] = {},
  ): Promise<T> {
    const seq = nextRequestSeq();
    try {
      return await super.request<T>(method, path, init);
    } catch (error) {
      const closed = noActiveProgramme(error);
      if (closed) notifyNoActiveProgramme(closed, seq);
      throw error;
    }
  }

  /**
   * §11.3 — the affiliate's links.
   *
   * The first call the portal makes that the affiliate can act on. Everything
   * else reports on traffic; this is where traffic comes from.
   */
  links(): Promise<{
    items: {
      link_id: string;
      campaign_name: string;
      brand_id: string;
      brand_name: string;
      landing_page: string | null;
      url: string;
      /** §11.6a — a short code minted for this link, on the same domain. */
      short_url?: string;
      created_at: string;
      revoked: boolean;
      labels: readonly string[];
      /** 0138 — set when the link is alive but its clicks are turned away. */
      stopped_because?: string;
    }[];
  }> {
    return this.request('GET', '/v1/links');
  }

  /* ---------------------------------------------------------------- *
   * #46 — smart links.
   *
   * One address over several campaigns, chosen when somebody presses it. The
   * token never changes when the pool does, which is the whole point: whatever
   * has already been printed or embedded keeps working.
   * ---------------------------------------------------------------- */

  smartLinks(): Promise<{ items: SmartLinkRow[]; note: string }> {
    return this.request('GET', '/v1/smart-links');
  }

  createSmartLink(body: {
    name: string;
    selection: string;
    members: { campaign_id?: string; category_id?: string }[];
  }): Promise<{ smart_link_id: string; name: string; token: string; members: number }> {
    return this.request('POST', '/v1/smart-links', { body });
  }

  updateSmartLink(
    smartLinkId: string,
    body: {
      name?: string;
      selection?: string;
      status?: string;
      members?: { campaign_id?: string; category_id?: string }[];
    },
  ): Promise<{ smart_link_id: string; status: string; message: string }> {
    return this.request('PUT', `/v1/smart-links/${smartLinkId}`, { body });
  }

  revokeSmartLink(smartLinkId: string): Promise<{ smart_link_id: string; message: string }> {
    return this.request('DELETE', `/v1/smart-links/${smartLinkId}`);
  }

  /**
   * §11.5 — stop a link from accepting new clicks. Never deletes it: traffic
   * already sent through it keeps its attribution, and it keeps showing up
   * in `links()`, marked revoked, rather than disappearing.
   */
  revokeLink(linkId: string): Promise<{ revoked: boolean }> {
    return this.request('POST', `/v1/links/${linkId}/revoke`);
  }

  /** §10.12a — only campaigns this affiliate is actually participating in. */
  /** 0226 — the operator's reporting buckets, so a report can be read one at a time. */
  /** Everything that happened on a day, traffic and money on one row. */
  detailedActivity(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<{
      day: string; currency: string | null;
      clicks: number; impressions: number; signups: number; ftd_count: number;
      deposits_minor: string; withdrawals_minor: string; chargebacks_minor: string;
      adjusted_net_deposits_minor: string; commission_minor: string;
    }[]>
  > {
    return this.request('GET', '/v1/reports/detailed-activity', { query: range });
  }

  /** Impressions and clicks per day, split by brand. */
  clicksImpressions(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<{ day: string; brand: string | null; impressions: number; clicks: number; reach: number }[]>
  > {
    return this.request('GET', '/v1/reports/clicks-impressions', { query: range });
  }

  /** §27.9f — which of my players are held out of revenue share, and by how much. */
  quarantine(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<{
      player_ref: string; currency: string;
      lifetime_ngr_minor: string; shortfall_minor: string;
    }[]> & { unavailable_because?: string }
  > {
    return this.request('GET', '/v1/reports/quarantine', { query: range });
  }

  campaignGroups(): Promise<{ items: { id: string; code: string; label: string }[] }> {
    return this.request('GET', '/v1/campaign-groups');
  }

  campaigns(): Promise<{
    /**
     * §10.11a — the campaigns you may ask for, kept apart from the ones you
     * are on. Nothing here can be turned into a link until the operator
     * accepts, so a picker that reads `items` must not read this.
     */
    discoverable: readonly {
      campaign_id: string;
      campaign_name: string;
      brand_id: string;
      brand_name: string;
      jurisdictions: readonly string[];
      products: readonly string[];
    }[];
    discoverable_note?: string;
    items: {
      campaign_id: string;
      campaign_name: string;
      brand_id: string;
      brand_name: string;
      campaign_version_id: string;
      /** Items 7/8 — the same read-only shape the operator's own detail view sees. */
      trafficback_url: string | null;
      postclick_anchor: string;
      postclick_goal_id: string | null;
      valid_from: string;
      /** When it stops taking traffic. Null is open-ended. */
      valid_to: string | null;
      /**
       * 0133 — the day the operator stops offering it, where one is set.
       *
       * Not `valid_to`, which is this *version's* validity and moves whenever
       * the campaign is re-versioned. This one is the offer's own end date,
       * and past it the click path answers CAMPAIGN_EXPIRED.
       */
      expires_at: string | null;
      jurisdictions: readonly string[];
      products: readonly string[];
      os_codes: readonly string[];
      device_classes: readonly string[];
      goals: readonly { id: string; key: string; name: string }[];
      landing_pages: readonly { landing_page_id: string; label: string; url: string }[];
      effective_attribution_window_days: number;
      /**
       * §39.13 — the share of this campaign's qualifying first deposits that
       * earn nothing. Zero is off, and reads as off: an affiliate who cannot
       * see that sampling is off cannot tell it from not being told.
       */
      sampling_rate_bps: number;
      /**
       * The operator has promoted this one. It grants nothing — it arrives on
       * the same participation-gated list as everything else, and a campaign
       * you cannot see is not featured *to you*.
       */
      featured: boolean;
      featured_position: number | null;
      /**
       * §7.12d — which terms pay you here, and the figures themselves, on the
       * list rather than one dialogue at a time. Null where nothing has been
       * agreed yet.
       */
      paid_on: 'CAMPAIGN' | 'DEFAULT' | 'CUSTOM' | null;
      terms: TermsSummary | null;
    }[];
    /** §10.10 — operator-invited, not yet joined. No link can be made for one. */
    offered: {
      campaign_id: string;
      campaign_name: string;
      brand_id: string;
      brand_name: string;
      participation_id: string;
      jurisdictions: readonly string[];
      products: readonly string[];
    }[];
  }> {
    return this.request('GET', '/v1/campaigns');
  }

  /** Brand invitations sent to the email this account signed up with. */
  myInvitations(): Promise<{
    items: {
      brand_id: string | null;
      brand: string | null;
      sent_at: string;
      expires_at: string;
    }[];
  }> {
    return this.request('GET', '/v1/me/invitations');
  }

  /**
   * §6.14c — answer one from the portal.
   *
   * `brandId` is the brand the card named, sent back so the server can refuse
   * to answer a different invitation than the one that was read.
   */
  respondToInvitation(
    brandId: string | null,
    decision: 'ACCEPT' | 'DECLINE',
  ): Promise<{ state: string; message: string }> {
    return this.request('POST', '/v1/me/invitations/respond', {
      body: { brand_id: brandId, decision },
    });
  }

  /**
   * §7.8 — the terms. Where the commercial rules actually live.
   *
   * Not on the campaign and not on the link: a link is a token saying who sent
   * the traffic. Confusing the two is how an affiliate comes to believe that
   * making a new link changed what they earn.
   */
  terms(brandId?: string): Promise<{
    items: {
      deal_id: string;
      /** §7.12d — PERSONAL: a deal with you. CAMPAIGN: a campaign's standard payout that prices you there. */
      terms_source?: 'PERSONAL' | 'CAMPAIGN';
      /** §26.2 — set only on a hybrid deal. */
      hybrid_mode?: string | null;
      brand: string;
      deal_code: string;
      /** 0307 — what the operator calls the deal; null when never named. */
      deal_name?: string | null;
      version: number;
      since: string;
      accepted_by_side: string | null;
      accepted_at: string | null;
      summary: string;
      revenue_basis: string;
      revshare_rate_bps: number | null;
      tier_mode: string | null;
      cpa_amount_minor: string | null;
      cpa_currency: string | null;
      cpa_minimum_deposit_minor: string | null;
      cpa_qualification_deadline_days: number | null;
      cpa_qualification_from?: 'SIGNUP' | 'FIRST_DEPOSIT' | null;
      cpa_hold_period_days: number | null;
      /** §24.5b — true when the hold above is the platform's standard rather than the deal's own. */
      cpa_hold_period_is_default?: boolean;
      /** §0.5b — where the hold in force came from. */
      cpa_hold_period_source?: 'DEAL' | 'BRAND' | 'OPERATOR' | 'PLATFORM';
      cpa_qualification_deadline_is_default?: boolean;
      /** §20.10 FTD_COUNT — a flat amount per qualified FTD, not a rate. */
      revshare_amount_per_unit_minor: string | null;
      revshare_amount_per_unit_currency: string | null;
      /** §24.2b — every enabled criterion, not only the minimum deposit. */
      cpa_criteria: { criterion: string; threshold_minor: string | null; threshold_count: number | null; label: string | null; standard?: boolean; applies?: boolean }[];
      /** §7.1/§14.10/§10.5 — narrows this deal instead of pricing every geo/product. */
      scopes: { dimension: string; value: string }[];
      /**
       * `label` is the platform's own word, null while nobody has set one
       * (0173). `kind`/`rate_bps`/`amount_minor`/`currency` are §20.4a's
       * widened shape (task A4): a RATE entry carries `rate_bps`, an AMOUNT
       * entry carries `amount_minor`/`currency`, and an entry recorded
       * before either kind existed carries neither (`kind: null`).
       */
      deduction_stack: {
        order: number;
        what: string;
        label: string | null;
        kind: 'AMOUNT' | 'RATE' | null;
        rate_bps: number | null;
        amount_minor: string | null;
        currency: string | null;
      }[];
      /** §24.9 — an extra payout on top of CPA/RevShare, never the §20.5 stack item above. */
      bonus_payout_enabled: boolean;
      bonus_payout_is_percentage: boolean;
      bonus_payout_basis: string | null;
      bonus_payout_rate_bps: number | null;
      bonus_payout_amount_minor: string | null;
      bonus_payout_currency: string | null;
      bonus_payout_window_days: number | null;
      /** The brand these terms are on, by id, so a screen can match a campaign to them. */
      brand_id: string;
      /**
       * §7.12 — set when these are terms for one campaign alone; null for the
       * default deal on the brand. A campaign's own payout is never listed
       * here: it is not an agreement with this affiliate, and the campaign
       * itself says it (`campaignTerms`).
       */
      campaign_id: string | null;
      campaign_name: string | null;
      /** §24.10 — what the CPA pays on. */
      cpa_trigger: string;
      /** §7.13 — where each component pays. Empty: everywhere. */
      coverage: readonly CoverageRow[];
      /**
       * §7.11 — an affiliate must be able to read what they are being asked
       * to accept, not only what they already accepted. §2.8b keeps this an
       * identifier: CPA/REVSHARE/HYBRID/RECURRING_CPA/CPL/CPI price a
       * player; FLAT/CPC price the affiliate itself (D1).
       */
      commission_type: string;
      /** §26.7 — the flat fee. Both null, or both set. */
      flat_fee_minor: string | null;
      flat_fee_currency: string | null;
      /** §26.10/§26.10a — paid per VALID click, up to the mandatory period cap. */
      cpc_amount_minor: string | null;
      cpc_currency: string | null;
      cpc_period_cap: number | null;
      /**
       * §26.11/§26.11a and §26.12/§8.10 — the API (`terms()`, terms.ts) has
       * sent these since Wave 8/7; this type had not been widened to match
       * (review finding, task A9/A8): an accepted CPM or spend-share deal's
       * own rate and cap did not reach this screen, only its one-line
       * summary.
       */
      cpm_amount_minor: string | null;
      cpm_currency: string | null;
      cpm_period_cap: number | null;
      spend_share_rate_bps: number | null;
      spend_period_cap_minor: string | null;
      spend_cap_currency: string | null;
      /** §7.8a/§31.2c — payout frequency and settlement terms, with the level each resolved from. */
      payout: { frequency: string; settlement_terms_days: number; source: string };
      /** §7.8b/§27.2b — the negative-balance policy in force, with its source. */
      negative_balance: { policy: string; carry_bps: number; source: string };
      /** §7.8e/§14.6b — the clamped cookie lifetime the edge actually sets. */
      cookie_lifetime_days: number;
      /** §7.8e — the attribution window (click to registration), always separate from the cookie lifetime above. */
      attribution_window_days: number;
      /** §14.6d — the CPA component's own window, or the deal's — a third figure, never collapsed into the two above. */
      cpa_window: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
      /** §14.6d — same, for RevShare. */
      revshare_window: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
      /** §14.6/§14.6c — whether the deal's own window above never expires. */
      attribution_window_never_expires: boolean;
      /** §24.7b — the CPA price list, rows by jurisdiction and channel. */
      cpa_price_rows: readonly { jurisdiction: string; channel: string; amount_minor: string }[];
      /** §24.7 — the deal-wide CPA-by-QFTD-count table. */
      cpa_tier_bands: readonly { from: number; to: number | null; amount_minor: string; currency: string }[];
      /** §26.3 — the deal-wide RevShare tier table. */
      revshare_tier_bands: readonly { from: number; to: number | null; rate_bps: number }[];
      /** §26.3a — per-product RevShare tier tables. */
      revshare_product_tiers: readonly { product: string; tiers: readonly { from: number; to: number | null; rate_bps: number }[] }[];
      /** §26.3/§26.3a — the one metric every tier table on this version is keyed on. */
      revshare_tier_key_metric: string | null;
      /** §26.3a — note about per-product QFTD tables, when applicable. */
      product_tier_qftd_note: string | null;
      /** §10.5/§25.5 — flat per-product overrides. */
      product_rates: readonly { product: string; revshare_rate_bps: number | null; cpa_amount_minor: string | null; cpa_currency: string | null }[];
      /** §8.7 (Addendum A) — the minimum payout threshold, with its source, and per-method detail. */
      minimum_payout: {
        amount_minor: string;
        currency: string;
        source: string;
        /** §30.4d — the affiliate's saved payment method, or null when none is saved. */
        method: string | null;
        /** §30.4d — the saved method's own minimum, or null when no method is saved or the operator has stated no figure for it. */
        method_amount_minor: string | null;
        method_currency: string | null;
        /** §30.4d — the figure that actually governs this affiliate's statements. */
        applies_amount_minor: string;
        applies_currency: string;
      };
      /** §7.14/§7.8f — this version's own stated traffic rules; empty or absent reads as not stated (7.14c). */
      traffic_rules: readonly { restriction: string; value: string; mode: string }[];
    }[];
    /** §6.16/§6.16a — set only for an agency account; null for DIRECT, which is every affiliate by default. */
    agency: { sub_account_count: number } | null;
    /** §7.12d — every campaign you take part in, and which terms pay you there. Absent from an older server. */
    campaigns?: readonly {
      campaign_id: string;
      campaign_name: string;
      brand_id: string;
      brand: string;
      /** False for a campaign left, whose players are still paid on it. */
      participating?: boolean;
      source: 'CAMPAIGN' | 'CUSTOM' | 'DEFAULT' | null;
      deal_id: string | null;
    }[];
  }> {
    return this.request('GET', '/v1/me/terms', {
      query: brandId ? { brand_id: brandId } : {},
    });
  }

  /** §7.11 — commercial terms proposed to this affiliate, awaiting their answer. */
  dealProposals(): Promise<{
    items: {
      deal_id: string;
      deal_code: string;
      deal_name?: string | null;
      brand: string;
      version: number;
      proposed_at: string;
      valid_from: string;
      summary: string;
      revenue_basis: string;
      revshare_rate_bps: number | null;
      cpa_amount_minor: string | null;
      cpa_currency: string | null;
      /** §24.7b — the proposed version's CPA price list (`pendingDealProposals`, terms.ts). */
      cpa_price_rows?: readonly { jurisdiction: string; channel: string; amount_minor: string }[];
      /**
       * QA round 2 F1 — the proposed version's tier tables, product rates and
       * component windows (`pendingDealProposals`, terms.ts, sends them all).
       * Optional so an older response reads as "none stated".
       */
      cpa_tier_bands?: readonly { from: number; to: number | null; amount_minor: string; currency: string }[];
      revshare_tier_bands?: readonly { from: number; to: number | null; rate_bps: number }[];
      revshare_product_tiers?: readonly { product: string; tiers: readonly { from: number; to: number | null; rate_bps: number }[] }[];
      revshare_tier_key_metric?: string | null;
      product_tier_qftd_note?: string | null;
      product_rates?: readonly { product: string; revshare_rate_bps: number | null; cpa_amount_minor: string | null; cpa_currency: string | null }[];
      cpa_window?: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
      revshare_window?: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
      cpa_criteria: { criterion: string; threshold_minor: string | null; threshold_count: number | null; label: string | null; standard?: boolean; applies?: boolean }[];
      scopes: { dimension: string; value: string }[];
      /**
       * `label` is the platform's own word, null while nobody has set one
       * (0173). `kind`/`rate_bps`/`amount_minor`/`currency` are §20.4a's
       * widened shape (task A4): a RATE entry carries `rate_bps`, an AMOUNT
       * entry carries `amount_minor`/`currency`, and an entry recorded
       * before either kind existed carries neither (`kind: null`).
       */
      deduction_stack: {
        order: number;
        what: string;
        label: string | null;
        kind: 'AMOUNT' | 'RATE' | null;
        rate_bps: number | null;
        amount_minor: string | null;
        currency: string | null;
      }[];
      /** §24.9 — see `terms()`'s own doc comment; same fields, same meaning. */
      bonus_payout_enabled: boolean;
      bonus_payout_is_percentage: boolean;
      bonus_payout_basis: string | null;
      bonus_payout_rate_bps: number | null;
      bonus_payout_amount_minor: string | null;
      bonus_payout_currency: string | null;
      bonus_payout_window_days: number | null;
      /** §7.12 — see `terms()`: set for an offer on one campaign alone, null for the default deal. */
      brand_id: string;
      campaign_id: string | null;
      campaign_name: string | null;
      cpa_trigger: string;
      coverage: readonly CoverageRow[];
      /**
       * §7.11 — an affiliate must be able to read what they are being asked
       * to accept, not only what they already accepted. Same fields, same
       * meaning as `terms()`'s own doc comments above.
       */
      commission_type: string;
      flat_fee_minor: string | null;
      flat_fee_currency: string | null;
      cpc_amount_minor: string | null;
      cpc_currency: string | null;
      cpc_period_cap: number | null;
      /**
       * §26.11/§26.11a and §26.12/§8.10 — the API (`pendingDealProposals`,
       * terms.ts) has sent these since Wave 8/7; this type had not been
       * widened to match (review finding, task A9/A8 — see the same note
       * on `PendingDealProposalsPanel`, App.tsx).
       */
      cpm_amount_minor: string | null;
      cpm_currency: string | null;
      cpm_period_cap: number | null;
      spend_share_rate_bps: number | null;
      spend_period_cap_minor: string | null;
      spend_cap_currency: string | null;
      payout: { frequency: string; settlement_terms_days: number; source: string };
      negative_balance: { policy: string; carry_bps: number; source: string };
      cookie_lifetime_days: number;
      attribution_window_days: number;
      minimum_payout: { amount_minor: string; currency: string; source: string };
      traffic_rules: readonly { restriction: string; value: string; mode: string }[];
    }[];
  }> {
    return this.request('GET', '/v1/me/deal-proposals');
  }

  /**
   * §7.12d — which terms pay you on one campaign, and what they say.
   *
   * Asked of the campaign rather than worked out from `terms()`, because the
   * campaign's own payout is not an agreement with this affiliate and never
   * appears there — and it is the one answer an affiliate cannot reach from
   * the list of their deals. A campaign they cannot see answers 404, the same
   * as one that does not exist.
   *
   * An offer in `pending` is answered with `respondDealProposal`, like any
   * other: it is the same proposal `dealProposals()` lists.
   */
  campaignTerms(campaignId: string): Promise<CampaignTerms> {
    return this.request('GET', `/v1/campaigns/${campaignId}/terms`);
  }

  respondDealProposal(
    dealId: string,
    version: number,
    decision: 'ACCEPT' | 'REJECT',
  ): Promise<{ state: string; note?: string }> {
    return this.request('POST', `/v1/me/deals/${dealId}/versions/${version}/respond`, {
      body: { decision },
    });
  }

  /** Item 15 — every version of this deal, old terms next to new. */
  dealHistory(dealId: string): Promise<{
    data: {
      version: number;
      state: string;
      commission_type: string;
      revenue_basis: string;
      revshare_rate_bps: number | null;
      cpa_amount_minor: string | null;
      cpa_currency: string | null;
      valid_from: string;
      valid_to: string | null;
      proposed_at: string;
      accepted_at: string | null;
      accepted_side: string | null;
      bonus_payout_enabled: boolean;
      bonus_payout_is_percentage: boolean;
      bonus_payout_basis: string | null;
      bonus_payout_rate_bps: number | null;
      bonus_payout_amount_minor: string | null;
      bonus_payout_currency: string | null;
      bonus_payout_window_days: number | null;
    }[];
    note: string;
  }> {
    return this.request('GET', `/v1/me/deals/${dealId}/history`, {});
  }

  /**
   * §26.2a — per-player CPA-recoupable balances. Invisible withholding is
   * indistinguishable from non-payment.
   */
  recoupment(brandId?: string): Promise<{
    items: {
      player_ref: string;
      brand: string;
      cpa_paid_minor: string;
      recouped_minor: string;
      outstanding_minor: string;
      currency: string;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/recoupment', {
      query: brandId ? { brand_id: brandId } : {},
    });
  }

  /** The brands this affiliate works with — not the operator's whole portfolio. */
  brands(): Promise<{
    items: {
      brand_id: string;
      brand_name: string;
      /** Brand profile (0331) — the logo's API path (prefix the API base), or null when none. */
      logo_url: string | null;
      /** The brand's own accent colour (`#rrggbb`), or null for the standard one. */
      theme_accent: string | null;
    }[];
  }> {
    return this.request('GET', '/v1/brands');
  }

  trackingDomains(
    campaignVersionId: string,
  ): Promise<{ items: { id: string; domain: string; is_default: boolean }[] }> {
    return this.request('GET', `/v1/campaigns/${campaignVersionId}/tracking-domains`);
  }

  createLink(
    campaignVersionId: string,
    subIds: readonly string[],
    landingPageId?: string,
    trackingDomainId?: string,
    /*
     * §11.6a — asked for on this link, or left to the operator's default
     * (`tracking.short_link_by_default`).
     *
     * The API has taken this from the affiliate route since #11, and this
     * client never sent it, so an affiliate could not have a short link
     * unless their operator had switched the default on — and that default
     * ships off, deliberately. The screen offered no way to ask for the one
     * thing the endpoint was willing to give.
     *
     * Undefined rather than false when the box is untouched: an absent field
     * takes the operator's default and an explicit false overrides it, which
     * is the tri-state `normalizeShortLinkInput` exists to keep.
     */
    shortLink?: boolean,
    /** §6.16 — which of the agency's own sub-accounts this link is filed under. Absent for a DIRECT affiliate. */
    subAccountId?: string,
    /** §11.9 — which channel this link is filed under. Absent when the affiliate files it under none. */
    channel?: string,
  ): Promise<{
    link_id: string;
    url: string;
    short_url?: string;
    labels?: readonly string[];
    sub_account?: { id: string; code: string; name: string };
    /** §11.9 — the channel this link is filed under, absent when it is filed under none. */
    channel?: string;
  }> {
    return this.request('POST', '/v1/links', {
      body: {
        campaign_version_id: campaignVersionId,
        sub_ids: subIds,
        ...(landingPageId ? { landing_page_id: landingPageId } : {}),
        ...(trackingDomainId ? { tracking_domain_id: trackingDomainId } : {}),
        ...(shortLink === undefined ? {} : { short_link: shortLink }),
        ...(subAccountId ? { sub_account_id: subAccountId } : {}),
        ...(channel ? { channel } : {}),
      },
    });
  }

  /**
   * §6.16 — the affiliate's own account kind and sub-accounts, read-only:
   * naming and archiving one is the operator's own act. Used by the link
   * creation form's picker, and by the by-sub-account report.
   */
  subAccounts(): Promise<{
    account_kind: string;
    items: { id: string; code: string; name: string; created_at: string; archived_at: string | null }[];
  }> {
    return this.request('GET', '/v1/me/sub-accounts');
  }

  /** §6.16 — this affiliate's own commission, grouped by sub-account. */
  subAccountReport(range: { from: string; to: string; brand_id?: string }): Promise<{
    data: {
      groups: {
        sub_account_id: string | null;
        sub_account_code: string | null;
        sub_account_name: string | null;
        archived: boolean;
        currency: string;
        line_count: number;
        amount_minor: string;
      }[];
    };
    data_as_of: string;
    is_provisional: boolean;
  }> {
    return this.request('GET', '/v1/reports/sub-accounts', { query: range });
  }

  /**
   * §6.15 — confirming an address.
   *
   * Anonymous: the token is who this is for. Every failure answers alike, so
   * this returns the outcome rather than throwing — a page that showed an
   * error for an expired link and a success for a used one would leak what
   * signup refused to say.
   */
  verify(
    token: string,
    password?: string,
  ): Promise<{ verified: boolean; message: string; password_required?: boolean; signed_in?: boolean }> {
    // A password is asked for (password_required) when the login behind the
    // link gets its password from this confirmation; sent again with it.
    return this.request('POST', '/v1/affiliates/verify', {
      body: password === undefined ? { token } : { token, password },
    });
  }

  /**
   * §6.15 — a fresh link, asked for from an old one. Anonymous, and the answer
   * is the same whatever the link was: it says a link is on its way if one
   * was needed, never whether one went.
   */
  resendVerification(token: string): Promise<{ message: string }> {
    return this.request('POST', '/v1/affiliates/verify/resend', { body: { token } });
  }

  /** The same, signed in: their own address, so they are told what happened. */
  resendMyConfirmation(): Promise<{ sent: boolean; message: string }> {
    return this.request('POST', '/v1/me/verification/resend');
  }

  /**
   * §6.14c — the whole offer, before any control. Anonymous by design: the
   * token is who this is for, and requiring a login would mean deciding before
   * seeing.
   */
  invitation(token: string): Promise<{
    valid: boolean;
    message?: string;
    offer?: {
      operator_legal_name: string;
      operator_trading_name: string;
      brands: readonly string[];
      /** Brand profile (0331) — the invited brand's logo API path; null for none or an all-brands invitation. */
      brand_logo_url: string | null;
      expires_at: string;
      terms: string;
      restrictions: readonly string[];
      will_see: readonly string[];
      will_not_see: readonly string[];
      starts_empty: string;
    };
    /** Whether they need to choose a password, or already have one (§2.3b). */
    needs_password?: boolean;
  }> {
    return this.request('GET', `/v1/invitations/${token}`);
  }

  /** §10.10 — accepting a campaign after being shown what it involves. */
  joinCampaign(campaignId: string): Promise<{ state: string; message: string }> {
    return this.request('POST', `/v1/campaigns/${campaignId}/join`);
  }

  /** §6.15 — programmes open to applications, whether signed in or not. */
  programmes(): Promise<{
    items: { code: string; operator_trading_name: string; brands: readonly string[] }[];
    relationship_note: string;
    review_note: string;
  }> {
    return this.request('GET', '/v1/programmes');
  }

  /** §6.15b — whose programme this is, before anybody types anything. */
  programme(code: string): Promise<{
    open: boolean;
    message?: string;
    operator_trading_name?: string;
  }> {
    return this.request('GET', `/v1/programmes/${encodeURIComponent(code)}`);
  }

  /** §6.15 — applying. The answer is the same whoever you are. */
  signUp(body: {
    code: string;
    email: string;
    display_name: string;
    country: string;
    phone?: string;
    password?: string;
    via: 'SELF_SIGNUP';
    /** §8.9b — a referral link's code, when this application followed one. */
    ref?: string;
  }): Promise<{ status?: string; message?: string }> {
    return this.request('POST', '/v1/affiliates/signup', { body });
  }

  acceptInvitation(
    token: string,
    body: { display_name: string; country: string; password?: string },
  ): Promise<{ accepted?: boolean; message?: string }> {
    return this.request('POST', `/v1/invitations/${token}/accept`, { body });
  }

  /**
   * §2.5c — which programmes this affiliate is on.
   *
   * The multi-operator view exists only on the platform's own portal; a
   * branded portal shows one programme and no switcher. Both resolve through
   * the same context, so this is presentation and not a second code path.
   */
  operators(): Promise<{
    items: {
      operator_id: string;
      legal_name: string;
      display_name: string;
      portal_status: string;
      is_active: boolean;
      /** 0332 — whether it can be opened; see programmes.ts. */
      selectable: boolean;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/operators');
  }

  selectOperator(operatorId: string): Promise<{ operator_id: string }> {
    return this.request('POST', `/v1/me/operators/${operatorId}/select`);
  }

  /** §2.8a — the language every communication is written in. */
  setLocale(locale: string): Promise<{ locale: string; note: string }> {
    return this.request('PUT', '/v1/me/locale', { body: { locale } });
  }

  /** The intro tour — which version this affiliate has seen (null: never). */
  tour(): Promise<{ seen_version: number | null; on_behalf: boolean }> {
    return this.request('GET', '/v1/me/tour');
  }

  markTourSeen(version: number): Promise<{ seen_version: number }> {
    return this.request('PUT', '/v1/me/tour', { body: { version } });
  }

  /**
   * §21.7 — the currency this affiliate reads their reports in (E38).
   *
   * Reports only. Nothing stored moves (§21.7a), and a statement or invoice
   * still shows the amount and currency it is billed in (§21.7f).
   */
  setDisplayCurrency(displayCurrency: string): Promise<{ display_currency: string; note: string }> {
    return this.request('PUT', '/v1/me/display-currency', {
      body: { display_currency: displayCurrency },
    });
  }

  /**
   * §21.7 / §1.3d — the active currencies, with how many minor-unit digits each
   * one is written with.
   *
   * The affiliate-side twin of `/v1/admin/currencies`, which this portal is
   * refused outright. `minor_unit_scale` is the field that matters here: it is
   * what tells `formatMoney` a JPY figure has no minor unit, and without it a
   * reader is shown a number a hundred times too large (`setCurrencyScales`).
   */
  currencies(): Promise<{ items: { code: string; name: string; minor_unit_scale: number }[] }> {
    return this.request('GET', '/v1/currencies');
  }

  me(): Promise<{
    affiliate_id: string;
    display_name: string;
    country: string;
    status: string;
    joined_at: string;
    attribution_model: string;
    email: string;
    /** §6.15 — whether that address has been confirmed. Absent from older servers. */
    email_confirmed?: boolean;
    status_explanation: string;
    /**
     * 0306 — the markets the operator records this affiliate as working, as
     * ISO country codes. The operator's record: shown, not edited. Absent
     * from older servers.
     */
    geos?: string[];
    /** 0220 — what the affiliate maintains themselves. */
    contact: {
      phone: string | null; company_name: string | null; telegram: string | null;
      teams: string | null; website_urls: string | null; marketing_note: string | null;
      newsletter_opt_in: boolean;
    };
    /** §6.7 — asked for and waiting on the operator, if anything is. */
    pending?: { legal_name?: string; country?: string };
    /** §3.6a — MUST be visibly banner-flagged wherever this is true. */
    impersonated: boolean;
    /**
     * §3.7 — whether a second factor is enrolled. Never the secret.
     *
     * A fact about the person signing in, not about the affiliate record:
     * one identity can span operators (§2.3), and it is the login that is
     * protected.
     */
    mfa_enrolled: boolean;
    /**
     * Item 8's TOTP flag, resolved for this affiliate's operator.
     *
     * It gates sign-in for an operator's own internal staff only — an
     * affiliate's second factor stays voluntary (§3.7). What it changes here
     * is what the Security panel says: an operator who has turned the flag on
     * expects one, and an affiliate offered a setup button with no word about
     * whether anybody wants it has been given a preference, not a reason.
     */
    operator_expects_mfa?: boolean;
    /**
     * §2.8a / §2.8d — the language chosen, and null when none has been.
     *
     * Two fields rather than one, because the picker and the display language
     * are different questions: showing the operator's default in the picker
     * turns "I have not chosen" into "I chose English".
     */
    locale: string | null;
    operator_default_locale: string;
    /**
     * §21.7 — the currency chosen to read reports in, and null when none has
     * been (E38). The same two-field shape as the locale above, for the same
     * reason: `ORIGINAL` is a choice — figures exactly as computed — and is not
     * the same answer as never having chosen.
     */
    display_currency: string | null;
    operator_default_display_currency: string;
  }> {
    return this.request('GET', '/v1/me');
  }

  /**
   * §22.7 — what each brand owes so far in a period that is not locked yet.
   * The statement answers this once a period is over; this answers it while
   * the affiliate is still in one.
   */
  currentEarnings(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<
      {
        brand_id: string;
        brand_name: string | null;
        currency: string;
        computed_minor: string;
        held_minor: string;
        pipeline_minor: string;
        pipeline_held_minor: string;
        pipeline_players: number;
        /** Of the pipeline, earned in dates the operator has not opened a period for yet. */
        no_period_minor: string;
        no_period_players: number;
        payable_minor: string;
        /** §24.2b — who is not earning and why: a criterion code, or NO_DEPOSIT_YET / NO_DEAL. */
        not_earning: { criterion: string; players: number }[];
        not_earning_players: number;
        /**
         * WP11 — first deposits whose CPA waits on an open review only, and
         * what the priced ones of them would earn once it ends.
         */
        under_review_players?: number;
        under_review_priced_players?: number;
        under_review_minor?: string;
        /** §24.5b — when the held qualifications release: earliest and latest. */
        held_release_from: string | null;
        held_release_to: string | null;
        /** When the last locked period on this brand ended; null if none has. */
        since: string | null;
        open_period_from: string | null;
        open_period_to: string | null;
      }[]
    > & {
      note: string;
      /** False when the operator has not opened a period covering today on this brand. */
      period_open: boolean;
    }
  > {
    return this.request('GET', '/v1/reports/current-earnings', { query: range });
  }

  /**
   * The commission earned in the chosen range, one figure per currency — the
   * dashboard tile that moves with the dates, unlike "earned so far" above.
   * Takes the dashboard's campaign and sub-ID filters too.
   */
  commissionInRange(range: {
    from: string; to: string; brand_id?: string; campaign_id?: string; sub_id?: string; sub_id_index?: string;
  }): Promise<Envelope<readonly { currency: string; amount_minor: string; lines: number }[]>> {
    return this.request('GET', '/v1/reports/commission-in-range', { query: range });
  }

  funnel(range: {
    from: string; to: string; brand_id?: string;
    /** The dashboard's own narrowing: one campaign, one sub-ID value. */
    campaign_id?: string; sub_id?: string; sub_id_index?: string;
  }): Promise<
    Envelope<
      {
        day: string;
        clicks: number;
        registrations: number;
        ftds: number;
        payable_ftds: number;
        bot_clicks: number;
        deduplicated_clicks: number;
        geo_blocked_clicks: number;
        /** Of those, the ones no country reached the edge for. */
        geo_unplaced_clicks?: number;
        os_blocked_clicks: number;
        device_blocked_clicks: number;
        /** #29 — refused by the campaign's own targeting rules. */
        targeting_blocked_clicks: number;
        /**
         * §12.13/§26.11 — the day's counted impressions, from
         * `impression_daily_counts`. Optional because a build deployed before
         * the funnel carried it answers without the field, and that is a
         * different thing from a day on which nothing was served.
         */
        impressions?: number;
      }[]
    >
  > {
    return this.request('GET', '/v1/reports/funnel', { query: range });
  }

  /** §11.2a / §39.6 — one row per link: which link, how many clicks, how many excluded and why. */
  linkReport(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<
      {
        link_id: string;
        campaign_name: string;
        brand_name: string;
        created: string;
        clicks: number;
        bot_clicks: number;
        duplicate_clicks: number;
        geo_blocked_clicks: number;
        /** Of those, the ones no country reached the edge for. */
        geo_unplaced_clicks?: number;
        os_blocked_clicks: number;
        device_blocked_clicks: number;
        /** #29 — refused by the campaign's own targeting rules. */
        targeting_blocked_clicks: number;
      }[]
    >
  > {
    return this.request('GET', '/v1/reports/links', { query: range });
  }

  /*
   * §21.7f — deliberately no `display_currency`. This is the per-line ledger a
   * statement and an invoice are built from, and its CSV is what an affiliate
   * reconciles a month against; restating those lines is the same failure
   * §21.7f names, reached a step earlier. The API does not accept the
   * parameter here either, for the reason `dateRange` gives about `brand_id`.
   */
  commission(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<
      {
        line_id: string;
        period_label: string;
        period_state: string;
        brand_name: string;
        type: string;
        amount_minor: string;
        currency: string;
        rule_ids: readonly string[];
        adjustment_reason_code: string | null;
        adjustment_justification: string | null;
        clawback_ground: string | null;
        /** §24.9f — only on a bonus payout line: the window, and the basis and rate in percentage mode. */
        bonus_payout_window_days: number | null;
        bonus_payout_is_percentage: boolean | null;
        bonus_payout_basis: string | null;
        bonus_payout_rate_bps: number | null;
      }[]
    >
  > {
    return this.request('GET', '/v1/reports/commission', { query: range });
  }

  /**
   * §17.8 — per-player reporting, which an operator may have switched off.
   *
   * The default is cohort-level (E27), so this failing is ordinary rather than
   * exceptional and the portal shows the cohort view instead of an error.
   */
  /*
   * The rows are `data`, the way every other paged report here names them —
   * the click log and the operator console's copy of this very report both do.
   *
   * This said `items`, which the server has never sent. TypeScript was
   * satisfied, so `report.data.items.map(...)` compiled, and three screens —
   * Players, Reports → Customer activity and Reports → Customer registrations
   * — threw "Cannot read properties of undefined (reading 'map')" on open. A
   * declared shape the server does not produce is not type safety; it is the
   * type checker agreeing with the mistake.
   */
  players(range: { from: string; to: string; cursor?: string }): Promise<
    Envelope<{
      data: Record<string, unknown>[];
      next_cursor?: string | null;
      has_more?: boolean;
    }>
  > {
    return this.request('GET', '/v1/reports/players', { query: range });
  }

  /**
   * The Registrations card's own breakdown: brand, country, count.
   *
   * Grouped by the server rather than counted off `players()` in the browser —
   * that list is cursor-paged, so a client-side count would total one page and
   * disagree with the card that opened it.
   */
  registrationBreakdown(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<readonly {
      brand: string;
      /** The registration jurisdiction (§36.2b(iii)), null where none recorded. */
      country: string | null;
      registrations: number;
      ftds: number;
    }[]> & { note: string }
  > {
    return this.request('GET', '/v1/reports/registrations/breakdown', { query: range });
  }

  /**
   * §21.7 — `display_currency` is the per-request override behind the
   * original-currency switch: this one report, asked in a different currency,
   * the way `brand_id` narrows this one report. It is not the saved preference
   * and does not change it; `ORIGINAL` asks for the figures as computed.
   */
  cohorts(range: { from: string; to: string; display_currency?: string }): Promise<
    Envelope<
      {
        cohort_month: string;
        /** null, with every figure, on a row under the brand's minimum cohort size. */
        players: number | null;
        ftds: number | null;
        deposits_minor: string | null;
        withdrawals_minor: string | null;
        ggr_minor: string | null;
        currency: string;
        pooled?: boolean;
        withheld?: boolean;
      }[]
    >
  > {
    return this.request('GET', '/v1/reports/cohorts', { query: range });
  }

  /**
   * §17.8b/§35.3 — the same cohorts, read by age as well as by month.
   *
   * Still cohort grain, so it needs no operator switch: unlike `players()`,
   * this one never fails for a permission reason, and the screen that shows
   * it does not carry a refusal path.
   */
  cohortAnalysis(range: { from: string; to: string; display_currency?: string }): Promise<
    Envelope<{
      checkpoints_months: number[];
      rows: {
        cohort_month: string;
        players: number | null;
        ftds: number | null;
        pooled?: boolean;
        withheld?: boolean;
        ftd_rate_pct: number | null;
        retained_players: number | null;
        retention_pct: number | null;
        deposits_minor: string | null;
        withdrawals_minor: string | null;
        ggr_minor: string | null;
        commission_minor: string | null;
        roi_ratio: number | null;
        currency: string;
      }[];
      matrix: {
        cohort_month: string;
        months_since: number;
        is_assessable: boolean;
        players: number | null;
        pooled?: boolean;
        withheld?: boolean;
        active_players: number | null;
        retention_pct: number | null;
        cumulative_deposits_per_player_minor: string | null;
        cumulative_ggr_per_player_minor: string | null;
        currency: string;
      }[];
      unattributed_commission: { amount_minor: string; currency: string }[];
      unattributed_because?: string;
      unavailable_because?: string;
      small_cohorts_because?: string;
    }>
  > {
    return this.request('GET', '/v1/reports/cohort-analysis', { query: range });
  }

  /**
   * §35.3a — the cohort report for this affiliate's own players: cohort by
   * checkpoint, anchored on registration or first deposit, split by one
   * dimension. Cohort grain, so it needs no player-level switch.
   */
  cohortMatrix(query: Record<string, string>): Promise<Envelope<CohortMatrixWire>> {
    return this.request('GET', '/v1/reports/cohort-matrix', { query });
  }

  /**
   * §35.3a — the players behind one cell. Player grain, so it is refused
   * (404) unless the operator has switched player-level reporting on.
   */
  cohortMatrixPlayers(query: Record<string, string>): Promise<
    Envelope<readonly CohortPlayerWire[]> & { has_more?: boolean }
  > {
    return this.request('GET', '/v1/reports/cohort-matrix/players', { query });
  }

  /**
   * §17.8b — whether this affiliate's cohort reports come in one shape only
   * (a brand protects small cohorts): no split, no filter, whole months. The
   * cohort screens ask first and offer only what will be answered.
   */
  cohortPrivacy(): Promise<{ breakdowns_off: boolean; min_cohort_size: number | null }> {
    return this.request('GET', '/v1/reports/cohort-privacy');
  }

  /**
   * §39.6 — every dimension recorded on the click, reportable.
   *
   * "Recording a dimension and not reporting on it is an unfulfilled promise
   * the affiliate made spending decisions against." The sub-ID boxes on the
   * links screen are exactly that promise: an affiliate who labels three
   * placements and cannot break down by label has been given a form that does
   * nothing.
   */
  performance(
    dimension: string,
    range: { from: string; to: string; brand_id?: string },
  ): Promise<Envelope<{
    value: string | null;
    clicks: number;
    bot_clicks: number;
    duplicate_clicks: number;
    geo_blocked_clicks: number;
    os_blocked_clicks: number;
    device_blocked_clicks: number;
    targeting_blocked_clicks: number;
  }[]>> {
    return this.request('GET', `/v1/reports/performance/${dimension}`, { query: range });
  }

  /** Ch. 33 — raise a formal disagreement, and read the ones already raised. */
  /**
   * §3.11 / E40 — authority platform support has asked for, in your name.
   *
   * Support can read your account and cannot act on it. For one thing —
   * raising a dispute — there is nobody else who can authorise it: the
   * dispute is against your operator, so your operator cannot approve it for
   * you. This is where they ask and where you answer.
   */
  agentMandates(): Promise<{
    items: readonly {
      id: string;
      act: string;
      /** Why they asked. Mandatory when the request was made. */
      reason: string;
      state: 'REQUESTED' | 'GRANTED' | 'USED' | 'REVOKED' | 'EXPIRED';
      requested_by_email: string | null;
      /** AF-01 WP9 — asked by AffDice support: shown as "AffDice team", the address is null. */
      requested_by_platform?: boolean;
      requested_at: string;
      expires_at: string | null;
      used_at: string | null;
      used_by_email: string | null;
      /** AF-01 WP9 — used by AffDice support: shown as "AffDice team". */
      used_by_platform?: boolean;
      produced_id: string | null;
    }[];
    awaiting_you: number;
  }> {
    return this.request('GET', '/v1/me/agent-mandates');
  }

  /** §3.11b — only you can give this, and only for one action at a time. */
  grantAgentMandate(id: string): Promise<{ mandate_id: string; state: string; expires_at: string; message: string }> {
    return this.request('POST', `/v1/me/agent-mandates/${id}/grant`, { body: {} });
  }

  /** §3.11d — take it back, at any time before it is used. */
  revokeAgentMandate(id: string): Promise<{ mandate_id: string; state: string; message: string }> {
    return this.request('POST', `/v1/me/agent-mandates/${id}/revoke`, { body: {} });
  }

  disputes(): Promise<{
    data: { id: string; category: string; kind: string; status: string;
            outcome: string | null; resolution: string | null;
            what_happened: string; raised_at: string; resolved_at: string | null;
            context_brand_id: string | null; brand_name: string | null;
            evidence: readonly DisputeEvidenceItem[] }[];
    asserted_count: number;
    note: string;
  }> {
    return this.request('GET', '/v1/disputes');
  }

  /**
   * Ch. 33 — raise one. Names its brand, always. `context` says where it was
   * raised from, and the server records what it held at that moment — the
   * click-discrepancy figures for that range, or the period's commission —
   * so the brand reads what the affiliate was looking at, not a retelling.
   */
  raiseDispute(body: {
    category: string; whatHappened: string; contextBrandId: string;
    campaignVersionId?: string; product?: string; periodId?: string;
    context?:
      | { source: 'CLICK_DISCREPANCY'; from: string; to: string; reportedClicks?: number }
      | { source: 'DISPUTES_FORM' };
    attachments?: readonly AttachmentUpload[];
  }): Promise<{
    dispute_id: string; kind: string; note: string;
  }> {
    return this.request('POST', '/v1/disputes', { body });
  }

  /** §33.4 — one file on the affiliate's own dispute, opened in a new tab. */
  openDisputeEvidence(disputeId: string, evidenceId: string): Promise<void> {
    return this.openInNewTab(`/v1/disputes/${disputeId}/evidence/${evidenceId}`, {});
  }

  /** §42.7 — their own history: everything that affected their money. */
  history(): Promise<{
    data: { action: string; action_kind: string; entity_type: string;
            reason: string | null; at: string; by_role: string }[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/audit');
  }

  /** §30.1a — every period, including the ones that earned nothing. */
  statements(brandId?: string): Promise<{
    data: {
      id: string;
      period_id: string;
      statement_no: string;
      currency: string;
      earned_minor: string;
      held_minor: string;
      payable_minor: string;
      carried_forward_minor: string;
      period_from: string;
      period_to: string;
      issued_at: string;
      is_correction: boolean;
      superseded: boolean;
      invoice_number: string | null;
    }[];
    /**
     * QA round 2 F4 — the revenue deficit still carried, per brand and
     * currency: the sum of every non-void carryover line, negative while a
     * deficit is owed back. Optional so a response from before the field
     * existed reads as "none".
     */
    carryover_deficits?: readonly CarryoverDeficit[];
    note: string;
  }> {
    return this.request('GET', '/v1/statements', {
      query: brandId ? { brand_id: brandId } : {},
    });
  }

  /** §24.2b — qualification status with failing criterion. */
  qualification(range: { from: string; to: string; brand_id?: string }): Promise<{
    data: { player_ref: string; status: string; failing_criterion: string | null; reason?: string | null; link_label?: string | null }[];
    note?: string;
  }> {
    return this.request('GET', '/v1/reports/qualification', { query: range });
  }

  /** §32.9 — our click counts vs theirs, with reasons. */
  discrepancy(range: {
    from: string; to: string; brand_id?: string; reported_clicks?: number;
    /** Item 9 — narrow the whole comparison to one campaign or one product. */
    campaign_id?: string; product?: string;
  }): Promise<{
    ours_valid: number; bot: number; duplicate: number; geo_blocked: number;
    os_blocked: number; device_blocked: number; targeting_blocked: number; ours_total: number;
    affiliate_tracker_total?: number; gap_vs_valid?: number;
    explained_rejects?: number; unexplained_gap?: number;
    by_sub_id?: readonly {
      sub_id: string; ours_valid: number; bot: number; duplicate: number; geo_blocked: number;
      os_blocked: number; device_blocked: number; targeting_blocked: number;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/reports/discrepancy', { query: queryParams(range) });
  }

  /** Affiliate invoices for periods that cleared the minimum. */
  invoices(): Promise<{
    data: {
      id: string; invoice_number: string; status: string; total_minor: string;
      currency: string; due_at: string; settled_minor: string; affiliate: string;
    }[];
  }> {
    return this.request('GET', '/v1/invoices');
  }

  /** §11.7 — promo codes issued to this affiliate. */
  promoCodes(): Promise<{
    data: { id: string; code: string; campaign_version_id: string | null;
            brand_id: string | null; valid_from: string; valid_to: string | null }[];
  }> {
    return this.request('GET', '/v1/promo-codes');
  }

  /** §10.6 — compliance-approved creatives for a campaign. */
  creatives(campaignId: string): Promise<{
    data: {
      id: string; type: string; language: string | null; asset_uri: string; status: string;
      /** Recorded by the operator on the creative; absent on one that carries none. */
      width?: number | null; height?: number | null;
      /** What the operator filed it under, for the gallery's filters. */
      name?: string | null; product?: string | null; geos?: readonly string[] | string | null;
    }[];
  }> {
    return this.request('GET', `/v1/campaigns/${campaignId}/creatives`);
  }

  /** §30.2 — one period, itemised, with the lines behind each figure. */
  statement(periodId: string): Promise<{
    statement: Record<string, unknown>;
    lines: {
      type: string; product: string | null; amount_minor: string; currency: string;
      state?: string; player_ref?: string | null;
      /** §30.2 — how the amount was counted: the basis a share was taken of, and the rate. */
      basis_amount_minor?: string | null; basis_kind?: string | null; rate_bps?: number | null;
      /** §11.2 — the campaign and placement that brought the player. */
      campaign?: string | null; link_label?: string | null;
      /** §8.2 — for a SUB_OVERRIDE, the recruit it was earned on. */
      source_affiliate?: string | null;
      /** The deal and the version that priced the line; terms for the version are in `deals`. */
      deal_id?: string | null;
      deal_version_id?: string | null;
      /** §20.4b — what came off GGR to reach the basis, with the running balance after each item. */
      deductions?: { item: string; amount_minor: string; running_total_minor: string }[];
      /** Why an adjustment or clawback moved money, as the operator recorded it. */
      adjustment_reason_code?: string | null;
      adjustment_justification?: string | null;
      clawback_ground?: string | null;
    }[];
    /**
     * The terms each deal version on the lines priced them on. Optional so a
     * response from before the field existed reads as "no terms to show".
     */
    deals?: CalculationDealInput[];
    carryover: { carried_forward_minor: string; note: string };
    /**
     * §39.13 — the conversions this period's campaigns declined to pay for.
     * Absent when none were: a block of zeroes invites the question of what
     * was sampled.
     */
    sampling?: {
      by_campaign: readonly { campaign: string; rate_bps: number; sampled_away: number }[];
      note: string;
    };
    note: string;
  }> {
    return this.request('GET', `/v1/statements/${periodId}`);
  }

  /** §24.2b — the period's first deposits that earned no CPA, per deal, counted by reason. */
  statementQualification(periodId: string): Promise<CalculationOutcomesInput> {
    return this.request('GET', `/v1/statements/${periodId}/qualification`);
  }

  payments(): Promise<{
    data: {
      document_no: string;
      net_minor: string;
      currency: string;
      due_at: string;
      operator_marked_paid_at: string | null;
      days_outstanding: number | null;
    }[];
    disclosure?: string;
  }> {
    return this.request('GET', '/v1/payments');
  }

  /**
   * §31.1 — the platform stores where the operator should send money. It holds
   * no payment credential and moves nothing.
   */
  /**
   * #34 — what is saved, so the form opens on it rather than on BANK_TRANSFER
   * and an empty box whatever the affiliate chose last time. `method` is null
   * until they have set one at all.
   */
  paymentPreference(): Promise<{
    method: string | null;
    label: string | null;
    details_url: string | null;
    details_url_label: string | null;
    /** §30.4 (0216) — the affiliate's own minimum, null where none is set. */
    minimum_payout_minor: string | null;
    minimum_payout_currency: string | null;
    note: string;
  }> {
    return this.request('GET', '/v1/me/payment-method');
  }

  setPaymentPreference(pref: {
    method: string;
    label?: string;
    /** #34 / §31.1b — where the details are kept. Never the details. */
    detailsUrl?: string | null;
    detailsUrlLabel?: string | null;
    /**
     * §30.4 — the affiliate's own minimum, which only ever raises the
     * operator's. Both halves or neither; two empty strings clear it.
     */
    minimumAmount?: string | null;
    minimumCurrency?: string | null;
  }): Promise<{ note: string }> {
    return this.request('PUT', '/v1/me/payment-method', { body: pref });
  }

  /**
   * §2.3a/§6.7/§3.7 — edit your own profile.
   *
   * Contact details apply at once. A legal name or country is *requested* and
   * the operator decides. The email needs an authenticator app first.
   */
  editProfile(body: {
    phone?: string | null; companyName?: string | null; telegram?: string | null;
    teams?: string | null; websiteUrls?: string | null; marketingNote?: string | null;
    newsletterOptIn?: boolean; legalName?: string | null; country?: string | null;
    email?: string | null;
  }): Promise<{ note: string }> {
    return this.request('PATCH', '/v1/me/profile', { body });
  }

  /**
   * §12.13 — one row per creative: served, clicked, and the ratio.
   *
   * Its own endpoint rather than a dimension of the Sources report, because
   * impressions are a daily aggregate and clicks are a ledger.
   */
  mediaReport(range: { from: string; to: string; brand_id?: string }): Promise<
    Envelope<{
      creative_id: string; type: string;
      width: number | null; height: number | null;
      impressions: number; clicks: number; reach: number;
    }[]>
  > {
    return this.request('GET', '/v1/reports/media', { query: range });
  }

  /* ---------------------------------------------------------------- *
   * Support — a question that is not a claim about money
   * ---------------------------------------------------------------- */

  /** Ask the operator something. Money claims go to Disputes instead. */
  raiseTicket(body: { subject: string; body: string; priority?: string }): Promise<{
    id: string; status: string; note: string;
  }> {
    return this.request('POST', '/v1/me/support-tickets', { body });
  }

  supportTickets(resolved: boolean): Promise<{
    data: {
      id: string; subject: string; priority: string; status: string;
      resolution: string | null; created_at: string; resolved_at: string | null;
    }[];
  }> {
    return this.request('GET', '/v1/me/support-tickets', {
      query: { resolved: resolved ? 'true' : 'false' },
    });
  }

  supportTicket(id: string): Promise<{
    data: {
      id: string; subject: string; body: string; priority: string; status: string;
      resolution: string | null; created_at: string; resolved_at: string | null;
      thread: { action: string; reason: string | null; by_role: string; at: string }[];
    };
  }> {
    return this.request('GET', `/v1/me/support-tickets/${id}`);
  }

  replyToTicket(id: string, message: string): Promise<{ status: string }> {
    return this.request('POST', `/v1/me/support-tickets/${id}/reply`, { body: { message } });
  }

  /* 0224 — the operator's segments, as this affiliate may choose from them. */
  customerGroups(): Promise<{
    items: { id: string; code: string; label: string; note: string | null; brand: string | null }[];
  }> {
    return this.request('GET', '/v1/me/customer-groups');
  }

  setLinkCustomerGroup(linkId: string, groupId: string | null): Promise<{ link_id: string }> {
    return this.request('PUT', `/v1/links/${linkId}/customer-group`, { body: { groupId } });
  }

  /* 0223 — asking the operator for a campaign, and what they said. */
  campaignRequests(): Promise<{
    items: {
      id: string; wanted_name: string; reason: string; status: string;
      decision_note: string | null; brand: string; created_at: string;
    }[];
  }> {
    return this.request('GET', '/v1/me/campaign-requests');
  }

  askForCampaign(body: { brandId: string; wantedName: string; reason: string }): Promise<{
    id: string; note: string;
  }> {
    return this.request('POST', '/v1/me/campaign-requests', { body });
  }

  /* 0222 — the affiliate's own labelling of their own links. */
  linkGroups(): Promise<{
    items: {
      id: string; label: string; note: string | null;
      spend_minor: string | null; spend_currency: string | null;
      links: number; earned_minor: string;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/link-groups');
  }

  createLinkGroup(body: {
    label: string; note?: string; spendAmount?: string; spendCurrency?: string;
  }): Promise<{ id: string }> {
    return this.request('POST', '/v1/me/link-groups', { body });
  }

  updateLinkGroup(id: string, body: {
    label: string; note?: string; spendAmount?: string; spendCurrency?: string;
  }): Promise<{ id: string }> {
    return this.request('PATCH', `/v1/me/link-groups/${id}`, { body });
  }

  setLinkGroup(linkId: string, groupId: string | null): Promise<{ link_id: string }> {
    return this.request('PUT', `/v1/links/${linkId}/group`, { body: { groupId } });
  }

  /* 0225 — credentials for reading your own reports from somewhere else. */
  apiClients(): Promise<{
    items: { id: string; label: string; client_id: string; created_at: string; last_used_at: string | null }[];
    enabled: boolean;
    note: string;
  }> {
    return this.request('GET', '/v1/me/api-clients');
  }

  createApiClient(label: string): Promise<{
    id: string; client_id: string; client_secret: string; note: string;
  }> {
    return this.request('POST', '/v1/me/api-clients', { body: { label } });
  }

  revokeApiClient(id: string): Promise<{ revoked: boolean }> {
    return this.request('POST', `/v1/me/api-clients/${id}/revoke`, { body: {} });
  }

  /** Every email we have sent, newest first. No new store — 0050 held them. */
  mailArchive(): Promise<{
    data: { id: string; subject: string; template_code: string; status: string;
            sent_at: string; is_new: boolean }[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/mail');
  }

  /** The operator's announcements, newest first, with what is new since last time. */
  news(): Promise<{
    data: { id: string; title: string; body: string; brand: string | null;
            published_at: string; is_new: boolean }[];
  }> {
    return this.request('GET', '/v1/me/news');
  }

  /** L10 — the notes the operator wrote for this affiliate to read. */
  myNotes(): Promise<{ data: { id: string; body: string; created_at: string; author_name: string | null }[] }> {
    return this.request('GET', '/v1/me/notes');
  }

  /**
   * §3.7a — the last ten sign-ins on this account, so their owner can
   * recognise one that is not theirs.
   */
  loginHistory(): Promise<{ data: { at: string; ip: string | null }[]; note: string }> {
    return this.request('GET', '/v1/me/logins');
  }

  /** §6.6/§6.6a — a channel the affiliate says they promote from. */
  trafficSources(): Promise<{
    declared: readonly {
      channel: string;
      property_url: string | null;
      declared_at: string;
      /** §6.6b — set only by a recorded act of the affiliate's account manager, never implied. */
      verified_at: string | null;
    }[];
    /** Codes only — never a label, which is English until this build translates the code itself. */
    active_channels: readonly string[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/traffic-sources');
  }

  /** Set semantics: the channels sent are the affiliate's whole declared list. */
  setTrafficSources(sources: readonly { channel: string; property_url?: string | null }[]): Promise<{
    declared: readonly {
      channel: string; property_url: string | null; declared_at: string; verified_at: string | null;
    }[];
    active_channels: readonly string[];
    note: string;
  }> {
    return this.request('PUT', '/v1/me/traffic-sources', { body: { sources } });
  }

  /** §26.12 — every media-spend claim this affiliate has made, and what happened to it. */
  spendDeclarations(): Promise<{
    items: readonly {
      id: string;
      deal_id: string;
      deal_code: string;
      period_id: string;
      period_starts_at: string;
      period_ends_at: string;
      declared_minor: string;
      currency: string;
      evidence_url: string | null;
      note: string | null;
      declared_at: string;
      state: 'DECLARED' | 'APPROVED' | 'REJECTED';
      approved_minor: string | null;
      decision_reason: string | null;
      decided_at: string | null;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/spend-declarations');
  }

  /** §26.12/§6.7c — declaring media spend for one deal and period. Prices nothing by itself (§26.12a). */
  declareSpend(body: {
    deal_id: string;
    period_id: string;
    declared_minor: string;
    currency: string;
    evidence_url?: string;
    note?: string;
  }): Promise<{ declaration_id: string; state: string; message: string }> {
    return this.request('POST', '/v1/me/spend-declarations', { body });
  }

  /** §41.7c — register a postback endpoint. */
  registerPostback(body: {
    event_type: string;
    url: string;
    secret: string;
  }): Promise<{ id: string; event_type: string; url: string; enabled: boolean }> {
    return this.request('POST', '/v1/postbacks', { body });
  }

  /** §41.7 — every postback this affiliate has registered, not removed. */
  postbacks(): Promise<{ items: PostbackRegistration[]; note: string }> {
    return this.request('GET', '/v1/postbacks');
  }

  /**
   * §41.7 — change the URL and/or rotate the secret; pause or resume.
   *
   * The event type is never here: it is not editable (operator conditions
   * and the delivery history both belong to the event a postback was
   * registered for). Omit `secret` to keep the one already stored — the
   * server never returns it, so there is nothing for this call to prefill
   * with; an empty string is a rotation to an empty secret, which the server
   * refuses (BAD_SECRET), not "leave it alone".
   */
  updatePostback(
    id: string,
    body: { url?: string; secret?: string; enabled?: boolean },
  ): Promise<{ id: string; event_type: string; url: string; enabled: boolean }> {
    return this.request('PATCH', `/v1/postbacks/${id}`, { body });
  }

  /**
   * §41.7 — a soft remove: `postback_deliveries` still references the row
   * and an operator reads it in a dispute, so this never deletes it. It stops
   * sending, disappears from this list, and 404s every other affiliate route.
   */
  removePostback(id: string): Promise<{ id: string; removed_at: string }> {
    return this.request('DELETE', `/v1/postbacks/${id}`);
  }

  /** §41.7b — one signed test request now. Writes nothing: not a delivery. */
  testPostback(id: string): Promise<PostbackTestResult> {
    return this.request('POST', `/v1/postbacks/${id}/test`);
  }

  /** §41.7b — delivery status for a registered postback. */
  postbackDeliveries(postbackId: string): Promise<{
    items: PostbackDelivery[];
    /** Over all rows, not only the page (the list itself is capped at 100). */
    withheld: number;
    note: string;
  }> {
    return this.request('GET', `/v1/postbacks/${postbackId}/deliveries`);
  }

  /** §33.4 — one dispute, with evidence we can disclose. */
  dispute(disputeId: string): Promise<{
    id: string;
    category: string;
    kind: string;
    status: string;
    outcome: string | null;
    resolution: string | null;
    resolution_summary: string | null;
    resolution_attachment_uri: string | null;
    what_happened: string;
    disputed_amount_minor: string | null;
    currency: string | null;
    campaign_version_id: string | null;
    product: string | null;
    context_brand_id: string | null;
    brand_name: string | null;
    evidence: readonly DisputeEvidenceItem[];
    raised_context: DisputeRaisedContext | null;
    /** Item 13 — the one reopen has been used; the form is not offered again. */
    reopened: boolean;
    raised_at: string;
    resolved_at: string | null;
    note: string;
  }> {
    return this.request('GET', `/v1/disputes/${disputeId}`);
  }

  /** Item 14 — every action taken on this dispute, in order — who, when, why. */
  disputeTimeline(disputeId: string): Promise<{
    data: { action: string; from_status: string | null; to_status: string | null;
            reason: string | null; by_role: string; at: string }[];
    note: string;
  }> {
    return this.request('GET', `/v1/disputes/${disputeId}/timeline`);
  }

  /** Item 14 — the affiliate's half of a NEEDS_INFO round trip. */
  replyToDispute(
    disputeId: string,
    message: string,
    attachments?: readonly AttachmentUpload[],
  ): Promise<{ status: string; note: string }> {
    return this.request('POST', `/v1/disputes/${disputeId}/reply`, {
      body: { message, ...(attachments?.length ? { attachments } : {}) },
    });
  }

  /** Item 13 — does not accept the decision; reopens it once, with a reason. */
  reopenDispute(disputeId: string, reason: string): Promise<{ status: string; note: string }> {
    return this.request('POST', `/v1/disputes/${disputeId}/reopen`, { body: { reason } });
  }

  /** §24.5b — held commission lines and when each becomes payable. */
  heldLines(): Promise<{
    items: {
      id: string;
      type: string;
      amount_minor: string;
      currency: string;
      brand: string;
      period_label: string;
      reason: string;
      release_on: string | null;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/me/held-lines');
  }

  /** §23.8 — calculation trace for a commission line. */
  commissionTrace(lineId: string): Promise<{
    id: string;
    type: string;
    amount_minor: string;
    currency: string;
    rule_ids: readonly string[];
    note: string;
    [key: string]: unknown;
  }> {
    return this.request('GET', `/v1/commission-lines/${lineId}/trace`);
  }

  /** Invoice detail. */
  invoice(invoiceId: string): Promise<{
    id: string;
    invoice_number: string;
    status: string;
    subtotal_minor: string;
    tax_minor: string;
    total_minor: string;
    currency: string;
    issued_at: string;
    due_at: string;
    settled_minor: string;
  }> {
    return this.request('GET', `/v1/invoices/${invoiceId}`);
  }

  /**
   * §39.6 — performance without a path dimension (query `dimension` instead).
   */
  performanceOverview(
    range: { from: string; to: string; brand_id?: string },
    dimension = 'campaign',
  ): Promise<Envelope<{
    value: string | null;
    clicks: number;
    bot_clicks: number;
    duplicate_clicks: number;
    geo_blocked_clicks: number;
    os_blocked_clicks: number;
    device_blocked_clicks: number;
    targeting_blocked_clicks: number;
  }[]>> {
    return this.request('GET', '/v1/reports/performance', {
      query: { ...range, dimension },
    });
  }

  /**
   * §17.8 / §22.2a — your players' activity day by day, on the operator's
   * accounting day. A lifetime total says whether a player is valuable; this
   * says when they stopped.
   */
  customerDaily(range: { from: string; to: string; brand_id?: string; limit?: string }): Promise<Envelope<{
    data: readonly {
      player_ref: string; day: string; turnover_minor: string; wins_minor: string;
      ggr_minor: string; deposits_minor: string; withdrawals_minor: string; currency: string;
    }[];
    has_more: boolean;
  }>> {
    return this.request('GET', '/v1/reports/customer-daily', { query: range });
  }

  /**
   * §39.6 — the clicks themselves, newest first.
   *
   * Every other traffic report here is a count, and a count cannot answer the
   * question an affiliate actually arrives with: "I clicked my own link and
   * nothing happened." The reasons a click did not count are recorded on it
   * and were shown nowhere.
   */
  clickLog(range: {
    from: string; to: string; brand_id?: string; limit?: string;
    /** §39.6 — the log's own two filters; every other report has neither. */
    country?: string; offset?: string;
    /** The dashboard's narrowing, so its Excluded figure opens to the same clicks. */
    campaign_id?: string; sub_id?: string; sub_id_index?: string;
    /** '1' — only the clicks the funnel counts as excluded (§12.5c). */
    excluded?: '1';
    /** '1' — only the clicks a Clicks figure counts. */
    counted?: '1';
  }): Promise<Envelope<{
    data: readonly {
      occurred_at: string;
      status: string;
      bot_reason: string | null;
      campaign: string;
      landing_page: string | null;
      link_token: string | null;
      sub_id_1: string | null;
      sub_id_2: string | null;
      sub_id_3: string | null;
      ip_country: string | null;
      /** §39.6 — the page the click came from, where the browser sent one. */
      referrer: string | null;
      /** Already on the click; the log simply never returned them. */
      brand: string | null;
      media: string | null;
      channel: string | null;
      device: string | null;
      os: string | null;
      browser: string | null;
      attributed: boolean;
      /** How many players this one click won; more than one when one browser registered twice. */
      players_won?: number;
      /**
       * Where the player this click won was registered — the brand's own
       * verified jurisdiction (§36.2b(iii)). Null for a click that won
       * nobody. A different question from `ip_country`, which is where the
       * click itself came from, and the two can legitimately differ.
       */
      player_country: string | null;
    }[];
    has_more: boolean;
  }>> {
    return this.request('GET', '/v1/reports/clicks', { query: range });
  }

  /** §39.11 — k-anonymised peer comparison. */
  benchmark(range: { from: string; to: string; brand_id?: string }): Promise<{
    data: { your_clicks: number; peer_median_clicks: number; peer_count: number } | null;
    withheld?: string;
    data_as_of?: string;
  }> {
    return this.request('GET', '/v1/reports/benchmark', { query: range });
  }

  /** §10.12c — accept, decline or withdraw a campaign participation. */
  respondParticipation(
    participationId: string,
    action: 'ACCEPT' | 'DECLINE' | 'WITHDRAW',
  ): Promise<{ state: string }> {
    return this.request('POST', `/v1/participations/${participationId}/respond`, {
      body: { action },
    });
  }

  /**
   * §8.9a/§8.9e — the referral link, terms, recruits and invitations, in one
   * read. Also what decides whether the Sub-affiliates tab shows at all
   * (`programme.enabled`).
   */
  mySubAffiliates(): Promise<SubAffiliatesReport> {
    return this.request('GET', '/v1/me/sub-affiliates');
  }

  /**
   * §8.9a — idempotent: an affiliate who already has a code gets the same one
   * back, never a second one.
   */
  createSubAffiliateReferralCode(): Promise<{ referral_code: string; signup_path: string | null }> {
    return this.request('POST', '/v1/me/sub-affiliates/referral-code');
  }

  /**
   * §8.9d/§6.14a — always answers `{ state: 'SENT', message: 'Invitation sent.' }`,
   * whether or not the address is already on the platform.
   */
  inviteSubAffiliate(email: string): Promise<{ state: string; message: string }> {
    return this.request('POST', '/v1/me/sub-affiliates/invitations', { body: { email } });
  }
}
